import { apiRequest } from '@/lib/api-client';

export type ChatProvider = 'anthropic' | 'openai';
export type ImageProvider = 'google' | 'openai' | 'cloudflare';
export type TtsProvider = 'elevenlabs' | 'openai' | 'google';
export type AudioProvider = 'local' | 'elevenlabs';

export interface AiSettings {
  chatProvider: ChatProvider;
  imageProvider: ImageProvider;
  ttsProvider: TtsProvider;
  /** Who supplies rendered-video ambience/SFX (local pack vs ElevenLabs). */
  audioProvider: AudioProvider;
  /** The model that will actually be used for this scope. */
  chatModel: string;
  /** The image model that will actually be used for this scope. */
  imageModel: string;
  hasOpenaiKey: boolean;
  hasAnthropicKey: boolean;
  hasGoogleKey: boolean;
  hasElevenLabsKey: boolean;
}

export interface UpdateAiSettingsBody {
  /**
   * Empty string clears the choice so the next scope up applies again — the
   * organisation for a project, the server default for an organisation.
   * Omitting the field leaves it unchanged.
   */
  chatProvider?: ChatProvider | '';
  imageProvider?: ImageProvider | '';
  ttsProvider?: TtsProvider | '';
  audioProvider?: AudioProvider | '';
  /** Model id within the provider's family; empty string resets to default. */
  chatModel?: string;
  imageModel?: string;
  openaiKey?: string;
  anthropicKey?: string;
  googleKey?: string;
  elevenLabsKey?: string;
}

/** Ambience / sound-effect source for rendered videos. */
export const AUDIO_PROVIDERS: { value: AudioProvider; label: string }[] = [
  { value: 'local', label: 'Local library — curated built-in ambience & SFX (recommended)' },
  { value: 'elevenlabs', label: 'ElevenLabs — AI-generated ambience & SFX (needs ElevenLabs key)' },
];

/** Narration (text-to-speech) provider choices for faceless video. */
export const TTS_PROVIDERS: { value: TtsProvider; label: string }[] = [
  { value: 'elevenlabs', label: 'ElevenLabs — most expressive narrator (recommended)' },
  { value: 'openai', label: 'OpenAI — expressive & cheap' },
  { value: 'google', label: 'Google Cloud TTS' },
];

/**
 * Chat provider choices.
 *
 * Exported like every other provider list rather than inlined in the settings
 * page, so the org form and the per-project override form cannot drift into
 * offering different options for the same field.
 */
export const CHAT_PROVIDERS: { value: ChatProvider; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
];

/** Creative-image provider choices. */
export const IMAGE_PROVIDERS: { value: ImageProvider; label: string }[] = [
  { value: 'google', label: 'Google Gemini (Nano Banana) — keeps your product (recommended)' },
  { value: 'openai', label: 'OpenAI — may alter product packaging' },
  {
    value: 'cloudflare',
    label: 'Cloudflare FLUX.2 — free daily allowance, lower fidelity',
  },
];

/** Curated image-model choices per provider (server env can override defaults). */
export const IMAGE_MODELS: Record<ImageProvider, { value: string; label: string }[]> = {
  google: [
    { value: 'gemini-3.1-flash-image', label: 'Nano Banana 2 — best quality (recommended)' },
    { value: 'gemini-3.1-flash-lite-image', label: 'Nano Banana 2 Lite — faster & cheaper' },
    { value: 'gemini-3-pro-image', label: 'Nano Banana Pro — highest fidelity' },
    { value: 'gemini-2.5-flash-image', label: 'Nano Banana — original' },
  ],
  openai: [{ value: 'gpt-image-1', label: 'GPT Image 1' }],
  cloudflare: [
    {
      value: '@cf/black-forest-labs/flux-2-klein-4b',
      label: 'FLUX.2 [klein] — takes reference images',
    },
  ],
};

/** Curated model choices per provider (server env can override the defaults). */
export const CHAT_MODELS: Record<ChatProvider, { value: string; label: string }[]> = {
  anthropic: [
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 — balanced (recommended)' },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 — most capable' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — fastest, cheapest' },
  ],
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o — balanced (recommended)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o mini — fastest, cheapest' },
  ],
};

const base = (orgId: string) => `/api/v1/organizations/${orgId}/ai-settings`;

/**
 * Per-project overrides.
 *
 * A project can pick a different provider from the rest of the organisation,
 * which is not a nicety: an explainer channel wants Cloudflare, where artwork
 * is drawn once into a reusable library and the free allowance covers it, while
 * a marketing project wants Gemini, which keeps the real product recognisable
 * in a creative. Anything left unset here falls through to the org setting.
 */
const projectBase = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/ai-settings`;

export const aiApi = {
  getOrgSettings: (orgId: string) => apiRequest<AiSettings>(base(orgId)),
  updateOrgSettings: (orgId: string, body: UpdateAiSettingsBody) =>
    apiRequest<AiSettings>(base(orgId), { method: 'PATCH', body }),
  getProjectSettings: (orgId: string, projectId: string) =>
    apiRequest<AiSettings>(projectBase(orgId, projectId)),
  updateProjectSettings: (orgId: string, projectId: string, body: UpdateAiSettingsBody) =>
    apiRequest<AiSettings>(projectBase(orgId, projectId), { method: 'PATCH', body }),
};
